import React from 'react';
import { createPortal } from 'react-dom';
import { useMap } from '@vis.gl/react-google-maps';

// OVERLAY VIEW FACTORY ---------------------------------------------------- //

interface HtmlOverlay extends google.maps.OverlayView {
  getContainer(): HTMLDivElement;
  updatePosition(position: google.maps.LatLngLiteral): void;
  updateZIndex(zIndex: number): void;
}

interface HtmlOverlayConstructor {
  new (position: google.maps.LatLngLiteral, zIndex: number): HtmlOverlay;
}

let HtmlOverlayClass: HtmlOverlayConstructor | null = null;

function getHtmlOverlayClass(): HtmlOverlayConstructor {
  if (HtmlOverlayClass) return HtmlOverlayClass;

  HtmlOverlayClass = class extends google.maps.OverlayView {
    private container: HTMLDivElement;
    private position: google.maps.LatLng;
    private zIndex: number;

    constructor(position: google.maps.LatLngLiteral, zIndex: number) {
      super();
      this.position = new google.maps.LatLng(position);
      this.zIndex = zIndex;
      this.container = document.createElement('div');
      this.container.style.position = 'absolute';
      this.container.style.transform = 'translate(-50%, -50%)';
      this.container.style.zIndex = String(zIndex);
      this.container.style.cursor = 'pointer';
    }

    getContainer() {
      return this.container;
    }

    updatePosition(position: google.maps.LatLngLiteral) {
      this.position = new google.maps.LatLng(position);
      this.draw();
    }

    updateZIndex(zIndex: number) {
      this.zIndex = zIndex;
      this.container.style.zIndex = String(zIndex);
    }

    onAdd() {
      const panes = this.getPanes();
      panes?.overlayMouseTarget?.appendChild(this.container);
    }

    draw() {
      const projection = this.getProjection();
      if (!projection) return;
      const point = projection.fromLatLngToDivPixel(this.position);
      if (!point) return;
      this.container.style.left = `${point.x}px`;
      this.container.style.top = `${point.y}px`;
    }

    onRemove() {
      this.container.remove();
    }
  } as HtmlOverlayConstructor;

  return HtmlOverlayClass;
}

// TYPES -------------------------------------------------------------------- //

interface HtmlMapMarkerProps {
  mapId: string;
  position: google.maps.LatLngLiteral;
  zIndex?: number;
  children: React.ReactNode;
}

// COMPONENT ---------------------------------------------------------------- //

const HtmlMapMarker = ({ mapId, position, zIndex = 1, children }: HtmlMapMarkerProps) => {
  const map = useMap(mapId);
  const overlayRef = React.useRef<HtmlOverlay | null>(null);
  const [container, setContainer] = React.useState<HTMLDivElement | null>(null);

  // Create/destroy overlay
  React.useEffect(() => {
    if (!map || position.lat == null || position.lng == null) return;

    const OverlayClass = getHtmlOverlayClass();
    const overlay = new OverlayClass(position, zIndex);
    overlay.setMap(map);
    overlayRef.current = overlay;
    setContainer(overlay.getContainer());

    return () => {
      overlay.setMap(null);
      overlayRef.current = null;
      setContainer(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Update position
  React.useEffect(() => {
    if (overlayRef.current && position.lat != null && position.lng != null) {
      overlayRef.current.updatePosition(position);
    }
  }, [position.lat, position.lng]);

  // Update zIndex
  React.useEffect(() => {
    if (overlayRef.current) {
      overlayRef.current.updateZIndex(zIndex);
    }
  }, [zIndex]);

  if (position.lat == null || position.lng == null || !container) return null;

  return createPortal(children, container);
};

export default HtmlMapMarker;
